/**
 * Navigation guards for route protection and redirect logic
 * Implements centralized navigation rules based on user authentication state
 */

import type { User } from '../types/user';
import { isPersonalInfoComplete } from './onboarding-completion';
import { hasActiveSubscription } from './subscription';

function getIncompleteOnboardingRedirect(user: User | null): string {
  if (isPersonalInfoComplete(user)) {
    return '/onboarding/terms';
  }

  return '/onboarding/personal-info';
}

export interface NavigationState {
  isAuthenticated: boolean;
  user: User | null;
  isProfileComplete: boolean;
}

export interface RouteRequirements {
  requireAuth?: boolean;
  requireEmailVerified?: boolean;
  requireProfileComplete?: boolean;
  requireActiveSubscription?: boolean;
  allowUnverified?: boolean;
  redirectIfAuthenticated?: boolean;
}

export interface NavigationResult {
  allowed: boolean;
  redirectTo?: string;
  reason?: string;
}

/**
 * Determines if a user can access a route based on requirements
 */
export function canAccessRoute(
  state: NavigationState,
  requirements: RouteRequirements = {}
): NavigationResult {
  const {
    requireAuth = false,
    requireEmailVerified = false,
    requireProfileComplete = false,
    requireActiveSubscription = false,
    redirectIfAuthenticated = false,
  } = requirements;

  // If route requires no authentication, allow access
  if (!requireAuth && !requireEmailVerified && !requireProfileComplete) {
    // But check if authenticated users should be redirected away
    if (redirectIfAuthenticated && state.isAuthenticated) {
      return {
        allowed: false,
        redirectTo: getDefaultRedirectForUser(state),
        reason: 'Already authenticated',
      };
    }
    return { allowed: true };
  }

  // Check authentication requirement
  if (requireAuth && !state.isAuthenticated) {
    return {
      allowed: false,
      redirectTo: '/auth/login',
      reason: 'Authentication required',
    };
  }

  // If not authenticated but auth is required by other checks
  if (!state.isAuthenticated && (requireEmailVerified || requireProfileComplete)) {
    return {
      allowed: false,
      redirectTo: '/auth/login',
      reason: 'Authentication required',
    };
  }

  // TODO: Re-enable email verification when backend email sending is configured
  // Check email verification requirement
  // if (requireEmailVerified && !state.user?.is_verified) {
  //   return {
  //     allowed: false,
  //     redirectTo: '/auth/verify-email',
  //     reason: 'Email verification required',
  //   };
  // }

  // Check if unverified users are allowed
  // if (!allowUnverified && !state.user?.is_verified) {
  //   return {
  //     allowed: false,
  //     redirectTo: '/auth/verify-email',
  //     reason: 'Email verification required',
  //   };
  // }

  // Check profile completion requirement
  if (requireProfileComplete && !state.isProfileComplete) {
    return {
      allowed: false,
      redirectTo: getIncompleteOnboardingRedirect(state.user),
      reason: 'Profile completion required',
    };
  }

  if (requireActiveSubscription && !hasActiveSubscription(state.user?.subscription)) {
    return {
      allowed: false,
      redirectTo: '/dashboard/manage-plan',
      reason: 'Active subscription required',
    };
  }

  return { allowed: true };
}

/**
 * Gets the default redirect path for an authenticated user based on their state
 */
export function getDefaultRedirectForUser(state: NavigationState): string {
  if (!state.isAuthenticated) {
    return '/';
  }

  // TODO: Re-enable email verification when backend email sending is configured
  // For now, skip email verification check to allow development/testing
  // if (!state.user?.is_verified) {
  //   return '/auth/verify-email'
  // }

  if (!state.isProfileComplete) {
    return getIncompleteOnboardingRedirect(state.user);
  }

  return '/dashboard';
}

/**
 * Route configuration with requirements
 */
export const ROUTE_REQUIREMENTS: Record<string, RouteRequirements> = {
  // Public routes
  '/': {},
  '/book/:linkCode': {},
  '/404': {},
  '/500': {},

  // Auth routes - redirect if already authenticated
  '/auth/login': { redirectIfAuthenticated: true },
  '/auth/register': { redirectIfAuthenticated: true },

  // Email verification routes - require auth but allow unverified
  '/auth/verify-email': { requireAuth: true, allowUnverified: true },
  '/auth/verify-email-success': { requireAuth: true, allowUnverified: true },

  // Onboarding routes - require verified email
  '/onboarding/personal-info': { requireAuth: true, requireEmailVerified: true },
  '/onboarding/terms': { requireAuth: true, requireEmailVerified: true },

  // Dashboard routes - require complete profile
  '/dashboard': { requireAuth: true, requireEmailVerified: true, requireProfileComplete: true },
  '/dashboard/booking-requests': {
    requireAuth: true,
    requireEmailVerified: true,
    requireProfileComplete: true,
  },
  '/dashboard/calendar': {
    requireAuth: true,
    requireEmailVerified: true,
    requireProfileComplete: true,
    requireActiveSubscription: true,
  },
  '/dashboard/bookings': {
    requireAuth: true,
    requireEmailVerified: true,
    requireProfileComplete: true,
    requireActiveSubscription: true,
  },
  '/dashboard/engagement-library': {
    requireAuth: true,
    requireEmailVerified: true,
    requireProfileComplete: true,
    requireActiveSubscription: true,
  },
  '/dashboard/clients': {
    requireAuth: true,
    requireEmailVerified: true,
    requireProfileComplete: true,
    requireActiveSubscription: true,
  },
  '/dashboard/shift-notes': {
    requireAuth: true,
    requireEmailVerified: true,
    requireProfileComplete: true,
    requireActiveSubscription: true,
  },
};

/**
 * Gets route requirements for a given path
 */
export function getRouteRequirements(path: string): RouteRequirements {
  // Try exact match first
  if (ROUTE_REQUIREMENTS[path]) {
    return ROUTE_REQUIREMENTS[path];
  }

  // Try pattern matching for dynamic routes
  for (const [pattern, requirements] of Object.entries(ROUTE_REQUIREMENTS)) {
    if (pattern.includes(':') && matchesPattern(path, pattern)) {
      return requirements;
    }
  }

  // Default to no requirements for unknown routes
  return {};
}

/**
 * Simple pattern matching for dynamic routes
 */
function matchesPattern(path: string, pattern: string): boolean {
  const pathParts = path.split('/');
  const patternParts = pattern.split('/');

  if (pathParts.length !== patternParts.length) {
    return false;
  }

  return patternParts.every((part, index) => {
    return part.startsWith(':') || part === pathParts[index];
  });
}

/**
 * Validates if a redirect URL is safe to use
 */
export function isSafeRedirectUrl(url: string): boolean {
  // Prevent open redirects by only allowing relative URLs
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//')) {
    return false;
  }

  // Must start with /
  if (!url.startsWith('/')) {
    return false;
  }

  // Prevent redirecting to auth pages to avoid loops
  if (url.startsWith('/auth/login') || url.startsWith('/auth/register')) {
    return false;
  }

  return true;
}
